import { Controller } from "@hotwired/stimulus"

// Hands off to the guided routine check (see FirstPlan) the first time a user lands on the home
// screen after onboarding. The server renders this controller, hidden, only while the flow is
// still owed; Turbo.visit takes it from there, presented as a modal on native by the path config
// and as a page on web.
//
// This mirrors feature_update_controller's localStorage claim for the same reason: Hotwire Native
// Android loads every bottom-tab WebView at launch, each with its own JS context, and the trigger
// lives in the shared layout. Per-context guards cannot dedupe across WebViews; localStorage is
// shared app-wide within the origin, so the claim collapses the launch render storm into one
// visit. The claim expires so a reset plus a cold relaunch can show it again while testing.
//
// The visibilitychange and pageshow re-checks cover the native case where this page was rendered,
// then sat behind the onboarding modal and the paywalls while the user worked through them. The
// tab comes back to the foreground with the trigger still in the DOM and nothing else to fire it.
const CLAIM_KEY = "firstPlanPresentedAt"
const CLAIM_WINDOW_MS = 10000

export default class extends Controller {
  static values = { url: String }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.removeOnClaim = (event) => this.removeIfClaimed(event)
    this.recheck = () => this.maybePresentOnReturn()

    window.addEventListener("storage", this.removeOnClaim)
    document.addEventListener("visibilitychange", this.recheck)
    window.addEventListener("pageshow", this.recheck)

    // Jitter so near-simultaneous tab loads don't all write at the same time.
    this.timeout = setTimeout(() => this.maybePresent(), 300 + Math.floor(Math.random() * 250))
  }

  disconnect() {
    clearTimeout(this.timeout)
    window.removeEventListener("storage", this.removeOnClaim)
    document.removeEventListener("visibilitychange", this.recheck)
    window.removeEventListener("pageshow", this.recheck)
  }

  // The tab came back to the foreground. If nobody has presented the flow in the meantime, this
  // trigger is still the one that owes it.
  maybePresentOnReturn() {
    if (document.visibilityState !== "visible") return
    if (!this.element.isConnected) return

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.maybePresent(), 200)
  }

  maybePresent() {
    if (this.recentlyClaimed) {
      this.removeTrigger()
      return
    }

    const claim = this.claim()
    if (claim) {
      this.timeout = setTimeout(() => this.presentIfClaimHeld(claim), 75)
      return
    }

    this.visit()
  }

  presentIfClaimHeld(claim) {
    if (!this.claimHeld(claim)) {
      this.removeTrigger()
      return
    }

    this.visit()
  }

  visit() {
    this.removeTrigger()
    window.Turbo.visit(this.urlValue)
  }

  removeIfClaimed(event) {
    if (event.key !== CLAIM_KEY) return
    if (event.newValue === null) return

    this.removeTrigger()
  }

  removeTrigger() {
    if (!this.element.isConnected) return

    clearTimeout(this.timeout)
    this.element.remove()
  }

  claim() {
    const claim = `${Date.now()}:${Math.random().toString(36).slice(2)}`

    try {
      localStorage.setItem(CLAIM_KEY, claim)
      return claim
    } catch {
      // localStorage unavailable (private mode / disabled): present anyway.
      return null
    }
  }

  claimHeld(claim) {
    try {
      return localStorage.getItem(CLAIM_KEY) === claim
    } catch {
      return false
    }
  }

  // The claim value is written as "<timestamp>:<random>"; parseInt stops at the colon, which is
  // how the age of the claim is read back.
  get recentlyClaimed() {
    try {
      const last = parseInt(localStorage.getItem(CLAIM_KEY) || "0", 10)
      return Date.now() - last < CLAIM_WINDOW_MS
    } catch {
      return false
    }
  }
}
